const STORAGE_KEY = 'rocha-last-routes';

const BLOCKED_PREFIXES = [
  '/login',
  '/forgot-password',
  '/reset-password',
  '/invite',
  '/email-verified',
];

export const isSafeAppPath = path => {
  if (typeof path !== 'string' || !path.startsWith('/') || path.startsWith('//')) {
    return false;
  }
  if (path.includes('://') || path.includes('\\') || path.includes('..')) {
    return false;
  }

  const pathname = path.split('?')[0];
  return !BLOCKED_PREFIXES.some(
    prefix => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );
};

const readRoutes = () => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
};

export const rememberRoute = (userId, companyId, path) => {
  if (!userId || !companyId || !isSafeAppPath(path)) {
    return;
  }

  const routes = readRoutes();
  const userKey = String(userId);
  const companyKey = String(companyId);
  routes[userKey] = {
    ...(routes[userKey] || {}),
    [companyKey]: path,
  };
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(routes));
};

export const clearRememberedRoutes = userId => {
  if (!userId) {
    sessionStorage.removeItem(STORAGE_KEY);
    return;
  }

  const routes = readRoutes();
  delete routes[String(userId)];
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(routes));
};

const permissionForPath = pathname => {
  if (
    pathname.startsWith('/lists') ||
    pathname.startsWith('/tasks') ||
    pathname.startsWith('/rocha-wms/kanban')
  ) {
    return 'list.view';
  }
  if (
    pathname.startsWith('/companies') ||
    pathname.startsWith('/company-users') ||
    pathname.startsWith('/roles')
  ) {
    return 'company.view';
  }
  if (pathname.startsWith('/reports')) {
    return 'reports.view';
  }
  if (
    pathname.startsWith('/rocha-wms/spaces') ||
    pathname.startsWith('/areas') ||
    pathname.startsWith('/projects')
  ) {
    return 'areas.view';
  }
  return null;
};

const savedPathAllowed = (saved, can) => {
  if (!isSafeAppPath(saved)) {
    return false;
  }
  if (typeof can !== 'function') {
    return true;
  }
  const permission = permissionForPath(saved.split('?')[0]);
  return permission ? can(permission) === true : true;
};

export const resolveAuthenticatedPath = ({ userId, companyId, can } = {}) => {
  if (!companyId) {
    return can?.('company.view') === false ? '/profile' : '/companies';
  }

  const saved = readRoutes()?.[String(userId)]?.[String(companyId)];
  if (isSafeAppPath(saved)) {
    return savedPathAllowed(saved, can) ? saved : '/rocha-wms/spaces';
  }

  if (!can || can('areas.view')) {
    return '/rocha-wms/spaces';
  }
  if (can('company.view')) {
    return '/companies';
  }
  if (can('list.view')) {
    return '/rocha-wms/kanban';
  }
  return '/profile';
};
